import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { RootState, useAppDispatch, useAppSelector } from '../../../state/state.tsx';
import { closeModal, toggleLoading } from '../../../state/ModalSlice/ModalSlice.ts';
import { useBackend } from '../../../hooks/backend/useBackend.ts';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { logAction } from '../../Common/utilityFunctions.ts';
import { ActionType } from '../../../enums.ts';

export default function DeleteBatchModal() {
    const Dispatch = useAppDispatch();
    const ServiceProviderID = useAppSelector(selectEffectiveUID);
    const open = useAppSelector((state: RootState) => state.ModalSlice.DeleteBatchModalOpen);
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
    const { subscriptionId, subscriptionName } =
        useAppSelector((state: RootState) => state.ArbitrarySlice.ForDeleteBatchModal);
    const { uid: spId, display_name: spName } =
        useAppSelector((state: RootState) => state.userSliceV2?.data) || {};

    const AllLiveSubscriptions = useAppSelector((state: RootState) => state.LiveSubscriptionSlice.data);
    const activeCount = AllLiveSubscriptions
        .filter((data) => data.subscriptionPlanRefId === subscriptionId && data.status !== 'Terminated')
        .length;

    const { remove } = useBackend(`/ServiceProviders/${ServiceProviderID}/Subscriptions`);

    function handleClose() {
        Dispatch(closeModal('DeleteBatchModalOpen'));
    }

    async function handleDelete(e: React.FormEvent) {
        e.preventDefault();
        if (activeCount > 0) return;
        Dispatch(toggleLoading(true));
        const response = await remove(subscriptionId);
        Dispatch(toggleLoading(false));
        if (response !== 'error') {
            logAction({
                action: ActionType.deleteBatch,
                timestamp: Timestamp.now(),
                platform: 'web',
                performedBy: {
                    spId: spId || '',
                    name: spName || '',
                },
                context: {
                    subscriptionId,
                    subscriptionName,
                },
            }, ServiceProviderID);
            toast.success(`Batch "${subscriptionName}" deleted successfully.`);
            handleClose();
        }
    }

    const hasActiveStudents = activeCount > 0;

    return (
        <React.Fragment>
            <Dialog
                sx={{ '& .MuiPaper-root': { borderRadius: '12px' } }}
                maxWidth="sm"
                fullWidth
                open={open}
            >
                <DialogContent sx={{ padding: '32px 40px' }} className="bg-white">
                    {/* Close */}
                    <div className="absolute right-3 top-3">
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>

                    {/* Header */}
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 6H5H21" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6L18.1 20.1C18.0455 20.6259 17.7908 21.1114 17.3875 21.4546C16.9841 21.7978 16.4634 21.9729 15.935 21.945L8.065 21.945C7.53664 21.9729 7.01593 21.7978 6.61256 21.4546C6.20919 21.1114 5.95453 20.6259 5.9 20.1L5 6H19Z" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">Delete Batch</h2>
                            <p className="text-sm text-gray-500 truncate max-w-[260px]" title={subscriptionName}>{subscriptionName}</p>
                        </div>
                    </div>

                    {/* Active students badge */}
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-4 ${hasActiveStudents ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                            <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke={hasActiveStudents ? "#D97706" : "#6B7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke={hasActiveStudents ? "#D97706" : "#6B7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke={hasActiveStudents ? "#D97706" : "#6B7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke={hasActiveStudents ? "#D97706" : "#6B7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span className={`text-sm font-medium ${hasActiveStudents ? 'text-amber-700' : 'text-gray-600'}`}>
                            Active students: <span className="font-bold">{activeCount}</span>
                        </span>
                    </div>

                    {/* Body */}
                    {hasActiveStudents ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5">
                            <p className="text-sm font-semibold text-red-700 mb-1">Cannot delete batch with active students</p>
                            <p className="text-sm text-red-600">
                                Please <strong>terminate</strong> and <strong>delete</strong> all{' '}
                                <strong>{activeCount}</strong> student{activeCount !== 1 ? 's' : ''} from this batch before deleting it.
                            </p>
                        </div>
                    ) : (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5">
                            <p className="text-sm font-semibold text-red-700 mb-1">⚠ This action is irreversible</p>
                            <p className="text-sm text-red-600">
                                Deleting this batch will permanently remove it and all its data. This cannot be undone.
                            </p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className="flex-1 border border-gray-300 rounded-lg py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        {!hasActiveStudents && (
                            <button
                                onClick={handleDelete}
                                disabled={isLoading}
                                className="flex-1 bg-red-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                            >
                                {isLoading && <CircularProgress size={16} sx={{ color: 'white' }} />}
                                {isLoading ? 'Deleting…' : 'Delete Batch'}
                            </button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}
